import type { MetricIdentity } from "@/metrics/catalog"
import {
  CompareByColorSection,
  type DistinctCompareByValue,
} from "@/run-detail/compare-by"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Tooltip,
} from "@coval/ui"
import { Eye, EyeOff } from "lucide-react"

import type { MetricViews } from "@/types/MetricViewConfig"

import {
  isTriageCategoryVisible,
  type ConversationTriageCategory,
  type ConversationTriageVisibility,
} from "../utils/reportTriage"
import { MetricViewsSection } from "./MetricViewsSection"

interface ReportSettingsSectionsProps {
  /** Classes for the wrapper, which only renders when a section does. */
  className?: string
  metricIds: string[]
  metricRecordsById: Record<string, MetricIdentity>
  metricViews: MetricViews
  onMetricViewsChange: (metricViews: MetricViews) => void
  distinctCompareByValues: DistinctCompareByValue[]
  compareByValueToColor: Record<string, string>
  setCompareByColor: (overrideKey: string, color: string) => void
  /**
   * Conversation Triage section only renders for reports linked to an HR
   * project. Omit these on surfaces with no HR context (e.g. the new-report page).
   */
  isHumanReviewLinked?: boolean
  triageCategories?: ConversationTriageCategory[]
  triageVisibility?: ConversationTriageVisibility
  onToggleTriageCategory?: (label: string, visible: boolean) => void
  isTriageLoading?: boolean
  humanReviewSimsError?: boolean
}

/**
 * The report's metric visibility, conversation triage, and compare-by colour
 * sections, as collapsible panels for the report's Display menu. Renders
 * nothing when no section applies, so the menu shows no empty divider.
 */
export function ReportSettingsSections({
  className,
  metricIds,
  metricRecordsById,
  metricViews,
  onMetricViewsChange,
  distinctCompareByValues,
  compareByValueToColor,
  setCompareByColor,
  isHumanReviewLinked = false,
  triageCategories = [],
  triageVisibility = {},
  onToggleTriageCategory = () => {},
  isTriageLoading = false,
  humanReviewSimsError = false,
}: ReportSettingsSectionsProps) {
  const showMetrics = metricIds.length > 0
  const showColors = distinctCompareByValues.length > 0
  const showTriage = isHumanReviewLinked

  const defaultOpen = [
    showMetrics ? "metrics" : null,
    showTriage ? "triage" : null,
    showColors ? "colors" : null,
  ].filter((value): value is string => value !== null)

  if (defaultOpen.length === 0) {
    return null
  }

  return (
    <div className={className}>
      <Accordion type="multiple" defaultValue={defaultOpen}>
        {showMetrics && (
          <AccordionItem value="metrics">
            <AccordionTrigger className="px-3">Metrics</AccordionTrigger>
            <AccordionContent className="pb-1">
              <MetricViewsSection
                metricIds={metricIds}
                metricRecordsById={metricRecordsById}
                metricViews={metricViews}
                onMetricViewsChange={onMetricViewsChange}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {showTriage && (
          <AccordionItem value="triage">
            <AccordionTrigger className="px-3">
              Conversation triage
            </AccordionTrigger>
            <AccordionContent className="pb-1">
              {isTriageLoading ? (
                <span className="block px-3 pb-2 text-xs text-coval-text-muted/60">
                  Loading…
                </span>
              ) : humanReviewSimsError ? (
                <span className="block px-3 pb-2 text-xs text-coval-danger-text">
                  Human review labels failed to load
                </span>
              ) : triageCategories.length === 0 ? (
                <span className="block px-3 pb-2 text-xs text-coval-text-muted/60">
                  No conversation triage labels
                </span>
              ) : (
                triageCategories.map((category) => {
                  const isVisible = isTriageCategoryVisible(
                    triageVisibility,
                    category.label,
                  )
                  return (
                    <Tooltip
                      key={category.label}
                      content={category.label}
                      // Left: a menu row's tooltip opening upward would cover the rows above it.
                      side="left"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          onToggleTriageCategory(category.label, !isVisible)
                        }
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-surface-1 cursor-pointer ${
                          isVisible
                            ? "text-coval-text-muted"
                            : "text-coval-text-muted/40"
                        }`}
                        aria-label={
                          isVisible
                            ? `Hide ${category.label}`
                            : `Show ${category.label}`
                        }
                      >
                        {isVisible ? (
                          <Eye className="h-3.5 w-3.5 shrink-0 text-coval-text-muted" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 shrink-0 text-coval-text-muted/40" />
                        )}
                        <span className="text-sm truncate">
                          {category.label}
                        </span>
                        <span className="ml-auto text-xs tabular-nums text-coval-text-muted/60">
                          {category.count}
                        </span>
                      </button>
                    </Tooltip>
                  )
                })
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {showColors && (
          <AccordionItem value="colors">
            <AccordionTrigger className="px-3">Colors</AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <CompareByColorSection
                distinctCompareByValues={distinctCompareByValues}
                compareByValueToColor={compareByValueToColor}
                setCompareByColor={setCompareByColor}
              />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  )
}
